/* The 48-hour security hold after a two-factor reset — the client's half.
 *
 * When an admin authorises a 2FA reset (server docs/2fa-recovery-and-reset.md,
 * part 4) the account carries `securityHoldUntil` for 48 hours. Until then the
 * server refuses the things that move value out — card and token withdrawals,
 * marketplace listings and purchases, wallet-address changes (and linking a
 * different wallet in the game), physical-card orders and shipping-address saves —
 * with 423 { code: 'SECURITY_HOLD', message, until }. Playing, packs, password
 * changes and deposits from the wallet already on the account stay open; a deposit
 * from a different connected wallet is refused too, because every secure operation
 * re-saves the connected wallet first.
 *
 * The portal learns of the hold from the sign-in response (`user.securityHoldUntil`,
 * kept in the stored user) and from GET /user/two-factor-reset, which the dashboard
 * notice polls; /auth/me carries it as well but the client discards that body. The
 * banner reads the stored user. A refusal's own message is
 * already the right words ("Locked until … (AEST) after your 2FA reset."), so
 * `holdMessage` only picks it out of whichever error shape the caller holds.
 */

export const SECURITY_HOLD_CODE = 'SECURITY_HOLD';

type HoldUser = { securityHoldUntil?: string | null } | null | undefined;

/** The moment the hold lifts, or null when there is no hold in force. */
export const holdUntil = (user: HoldUser, now = Date.now()): Date | null => {
  const iso = user?.securityHoldUntil;
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) && t > now ? new Date(t) : null;
};

/** "12 Oct 2026, 3:40 pm AEDT" — the same words the server's refusal uses. The zone
 *  label comes from the formatter: AEST in winter, AEDT in summer. */
export const formatHoldUntil = (until: Date | string) =>
  new Date(until).toLocaleString('en-AU', {
    timeZone: 'Australia/Sydney',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  });

/** The server's own refusal, if this error is the hold — else null. Reads both
 *  shapes in use: useNetwork's ApiError (`.data`) and a raw axios error (`.response.data`). */
export const holdMessage = (error: unknown): string | null => {
  const e = error as { data?: any; response?: { data?: any } } | null | undefined;
  const data = e?.data ?? e?.response?.data;
  if (!data || data.code !== SECURITY_HOLD_CODE) return null;
  if (typeof data.message === 'string' && data.message) return data.message;
  return data.until
    ? `Locked until ${formatHoldUntil(data.until)} after your 2FA reset.`
    : 'Locked after your 2FA reset. Please try again later.';
};
